/**
 * Service levels: the board, and the escalation ladder.
 *
 * The board is sorted by **least time remaining**, not by priority. A comfortable P1 above a P3
 * about to breach hides the ticket somebody needs to pick up in the next ten minutes, which is the
 * one question this screen exists to answer.
 *
 * A paused ticket shows `paused` rather than a countdown. It is not counting down, and ranking it
 * against tickets that are would send somebody to chase a client who has already been chased.
 */

import { useCallback, useState, type JSX } from 'react';

import { api, type ClockReading } from '../shared/api/client';
import {
  Badge,
  Card,
  ErrorBox,
  Formula,
  PageHeader,
  Spinner,
  Stat,
  StatRow,
  Table,
  type Tone,
} from '../shared/ui/ui';
import { useAsync } from '../shared/ui/useAsync';

const CLOCK_TONE: Record<string, Tone> = {
  ok: 'ok',
  met: 'ok',
  at_risk: 'warn',
  breached: 'danger',
  missed_target: 'danger',
  paused: 'muted',
};

/** Minutes as something a person reads without doing arithmetic. */
function humanize(minutes: number): string {
  const abs = Math.abs(minutes);
  const rendered =
    abs < 60 ? `${String(abs)}m` : abs < 1440 ? `${String(Math.round(abs / 60))}h` : `${String(Math.round(abs / 1440))}d`;
  return minutes < 0 ? `${rendered} over` : rendered;
}

function ClockCell({ reading }: { reading: ClockReading }): JSX.Element {
  return (
    <>
      <Badge tone={CLOCK_TONE[reading.status] ?? 'muted'}>{reading.status.replace('_', ' ')}</Badge>
      <Formula>
        {humanize(reading.elapsed_minutes)} of {humanize(reading.target_minutes)}
        {reading.status !== 'paused' && ` · ${humanize(reading.remaining_minutes)} left`}
      </Formula>
    </>
  );
}

export function SlaBoard(): JSX.Element {
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => {
    setRevision((r) => r + 1);
  }, []);

  const { data, error, loading } = useAsync(() => api.slaBoard(200), [revision]);
  const escalations = useAsync(() => api.escalations(100), [revision]);
  const [sweeping, setSweeping] = useState(false);
  const [sweepResult, setSweepResult] = useState<string | null>(null);
  const [writeError, setWriteError] = useState<unknown>(null);

  const rows = data ?? [];
  const breached = rows.filter(
    (r) => r.response.status === 'breached' || r.resolve.status === 'breached',
  ).length;
  const atRisk = rows.filter(
    (r) => r.response.status === 'at_risk' || r.resolve.status === 'at_risk',
  ).length;
  const paused = rows.filter((r) => r.resolve.status === 'paused').length;

  const runSweep = (): void => {
    setSweeping(true);
    setWriteError(null);
    void api
      .runSlaSweep()
      .then((result) => {
        // `already_known` is shown, not hidden. It is the number that proves the ladder is
        // idempotent — a sweep reporting the same breach every minute is a sweep about to be muted.
        setSweepResult(
          `examined ${String(result.examined)}, raised ${String(result.raised)}, already known ${String(result.already_known)}`,
        );
        refresh();
      })
      .catch((e: unknown) => {
        setWriteError(e);
      })
      .finally(() => {
        setSweeping(false);
      });
  };

  return (
    <>
      <PageHeader
        title="Service levels"
        subtitle="What was promised, what is left, and what has already been missed. Clocks stop while a ticket waits on the client."
      />

      <ErrorBox error={error} />
      <ErrorBox error={escalations.error} />
      <ErrorBox error={writeError} />

      <Card>
        <StatRow>
          <Stat label="Live tickets" value={rows.length} />
          <Stat
            label="Breached"
            value={breached}
            tone={breached > 0 ? 'danger' : 'neutral'}
            hint="past target and still running"
          />
          <Stat
            label="At risk"
            value={atRisk}
            tone={atRisk > 0 ? 'warn' : 'neutral'}
            hint="past the warning threshold"
          />
          <Stat
            label="Paused"
            value={paused}
            hint="waiting on the client — not counting down"
          />
        </StatRow>
      </Card>

      <Card
        title="The board"
        subtitle="Least time remaining first, on whichever clock is closer to breaching — not by priority."
      >
        {loading ? (
          <Spinner />
        ) : (
          <Table
            columns={['Priority', 'Ticket', 'State', 'Owner', 'Response', 'Resolution']}
            empty="No live tickets. Clocks run only while a ticket is open."
          >
            {rows.map((r) => (
              <tr key={r.ticket_id}>
                <td>
                  <Badge tone={r.priority === 'p1' ? 'danger' : r.priority === 'p2' ? 'warn' : 'muted'}>
                    {r.priority}
                  </Badge>
                </td>
                <td className="table__strong">{r.reference}</td>
                <td>
                  <Formula>{r.state.replace('_', ' ')}</Formula>
                </td>
                <td>
                  {r.assignee_id === null ? (
                    <Badge tone="warn">unassigned</Badge>
                  ) : (
                    <Formula>{r.assignee_id.slice(0, 8)}</Formula>
                  )}
                </td>
                <td>
                  <ClockCell reading={r.response} />
                </td>
                <td>
                  <ClockCell reading={r.resolve} />
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Card
        title="Escalation ladder"
        subtitle="One escalation per ticket, clock, and level. Running the sweep again does not raise the same breach twice — that is what keeps the channel worth listening to."
      >
        <div className="inline-form">
          <button type="button" className="button" disabled={sweeping} onClick={runSweep}>
            {sweeping ? 'Sweeping…' : 'Run the sweep'}
          </button>
          {sweepResult !== null && <Formula>{sweepResult}</Formula>}
        </div>

        {escalations.loading ? (
          <Spinner />
        ) : (
          <Table
            columns={['Raised', 'Ticket', 'Clock', 'Level', 'Reading', 'Owner then']}
            empty="Nothing has escalated. Run the sweep to check."
          >
            {(escalations.data ?? []).map((e) => (
              <tr key={`${e.ticket_id}-${e.clock}-${e.level}`}>
                <td>
                  <Formula>{e.raised_at.replace('T', ' ').slice(0, 16)}</Formula>
                </td>
                <td>
                  <div className="table__strong">{e.reference}</div>
                  {/* Rendered as text. A ticket title is attacker-controlled. */}
                  <Formula>{e.title}</Formula>
                </td>
                <td>
                  <Formula>{e.clock}</Formula>
                </td>
                <td>
                  <Badge tone={e.level === 'breached' ? 'danger' : 'warn'}>
                    {e.level.replace('_', ' ')}
                  </Badge>
                </td>
                <td>
                  {/* The reading when it fired, not a recomputation — the ticket kept moving. */}
                  <Formula>
                    {humanize(e.elapsed_minutes)} of {humanize(e.target_minutes)}
                  </Formula>
                </td>
                <td>
                  {e.assignee_id === null ? (
                    <Badge tone="danger">nobody</Badge>
                  ) : (
                    <Formula>{e.assignee_id.slice(0, 8)}</Formula>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <SlaTargets />
    </>
  );
}

/** The promises themselves, read-only here — changing them is a tenant-admin act. */
function SlaTargets(): JSX.Element {
  const { data, error, loading } = useAsync(() => api.slaPolicy());

  return (
    <Card
      title="Targets"
      subtitle="Minutes to first response and to resolution, by priority. Every clock runs 24/7 — business-hours calendars are deliberately not approximated."
    >
      <ErrorBox error={error} />
      {loading ? (
        <Spinner />
      ) : data === null ? (
        <Formula>No policy returned.</Formula>
      ) : (
        <Table columns={['Priority', 'Respond within', 'Resolve within']}>
          {(['p1', 'p2', 'p3', 'p4'] as const).map((p, i) => (
            <tr key={p}>
              <td>
                <Badge tone={p === 'p1' ? 'danger' : p === 'p2' ? 'warn' : 'muted'}>{p}</Badge>
              </td>
              <td className="table__num">{humanize(data.response_minutes[i] ?? 0)}</td>
              <td className="table__num">{humanize(data.resolve_minutes[i] ?? 0)}</td>
            </tr>
          ))}
        </Table>
      )}
      {data !== null && (
        <Formula>
          At risk at {data.at_risk_percent}% of target · {data.round_the_clock ? '24/7' : 'business hours'}
        </Formula>
      )}
    </Card>
  );
}
